import axios from 'axios';
import { getApiBaseUrl } from '../config/environment';

export interface User {
  id: number;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface AuthResponse {
  success: boolean;
  isAuthenticated?: boolean;
  user?: User | null;
  error?: string;
  message?: string;
}

const API_ENDPOINT = '/rest/api';

class AuthService {
  private user: User | null = null;
  private isAuthenticated: boolean = false;
  private isInitialized: boolean = false;

  /**
   * Überprüft die aktuelle Session beim App-Start
   */
  async checkSession(): Promise<AuthResponse> {
    try {
      const response = await axios.get(`${getApiBaseUrl()}${API_ENDPOINT}`, {
        params: {
          'tx_buchungsmanager_frontend[action]': 'session-check'
        }
      });

      const data: AuthResponse = response.data;
      
      if (data.success && data.isAuthenticated && data.user) {
        this.user = data.user;
        this.isAuthenticated = true;
      } else {
        this.user = null;
        this.isAuthenticated = false;
      }
      
      this.isInitialized = true;
      return data;
    } catch (error) {
      console.error('Session check failed:', error);
      this.user = null;
      this.isAuthenticated = false;
      this.isInitialized = true;
      
      return {
        success: false,
        isAuthenticated: false,
        user: null,
        error: 'Fehler bei der Session-Überprüfung'
      };
    }
  }

  /**
   * Login mit Username und Password
   */
  async login(username: string, password: string): Promise<AuthResponse> {
    try {
      const response = await axios.post(`${getApiBaseUrl()}${API_ENDPOINT}`, 
        { 
          username, 
          password 
        },
        {
          params: {
            'tx_buchungsmanager_frontend[action]': 'login'
          },
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const data: AuthResponse = response.data;
      
      if (data.success && data.user) {
        this.user = data.user;
        this.isAuthenticated = true;
      }
      
      return data;
    } catch (error) {
      console.error('Login failed:', error);
      
      return {
        success: false,
        error: 'Anmeldung fehlgeschlagen'
      };
    }
  }

  /**
   * Logout - Session beenden
   */
  async logout(): Promise<AuthResponse> {
    try {
      const response = await axios.post(`${getApiBaseUrl()}${API_ENDPOINT}`, {},
        {
          params: {
            'tx_buchungsmanager_frontend[action]': 'logout'
          }
        }
      );

      this.user = null;
      this.isAuthenticated = false;
      
      return response.data;
    } catch (error) {
      console.error('Logout failed:', error);
      
      // Auch bei Fehler lokal ausloggen
      this.user = null;
      this.isAuthenticated = false;
      
      return {
        success: false,
        error: 'Fehler beim Abmelden'
      };
    }
  }

  // Getter
  getUser(): User | null {
    return this.user;
  }

  getIsAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  getIsInitialized(): boolean {
    return this.isInitialized;
  }
}

// Singleton Instance
export const authService = new AuthService();

// React Hook für Auth State
import { useState, useEffect } from 'react';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(authService.getUser());
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(authService.getIsAuthenticated());
  const [isInitialized, setIsInitialized] = useState<boolean>(authService.getIsInitialized());
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const checkSession = async () => {
    setIsLoading(true);
    const result = await authService.checkSession();
    setUser(authService.getUser());
    setIsAuthenticated(authService.getIsAuthenticated());
    setIsInitialized(authService.getIsInitialized());
    setIsLoading(false);
    return result;
  };

  const login = async (username: string, password: string) => {
    setIsLoading(true);
    const result = await authService.login(username, password);
    setUser(authService.getUser());
    setIsAuthenticated(authService.getIsAuthenticated());
    setIsLoading(false);
    return result;
  };

  const logout = async () => {
    setIsLoading(true);
    const result = await authService.logout();
    setUser(authService.getUser());
    setIsAuthenticated(authService.getIsAuthenticated());
    setIsLoading(false);
    return result;
  };

  return {
    user,
    isAuthenticated,
    isInitialized,
    isLoading,
    checkSession,
    login,
    logout
  };
}; 